import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import Logo from '../components/Logo';

export default function Register() {
  const navigate = useNavigate();

  // Development mode: pre-fill password with 'Foundation1!'
  const isDev = (import.meta as { env?: { MODE?: string } }).env?.MODE === 'development' || window.location.hostname === 'localhost';
  const defaultPassword = isDev ? 'Foundation1!' : '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(defaultPassword);
  const [confirmPassword, setConfirmPassword] = useState(defaultPassword);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Client-side validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 12) {
      setError('Password must be at least 12 characters long');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.register(email, password);
      setSuccess(response.message);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (password.length < 12) return 'bg-red-400';
    if (password.length < 16) return 'bg-yellow-400';
    return 'bg-green-400';
  };

  const getPasswordStrengthText = () => {
    if (password.length === 0) return '';
    if (password.length < 12) return 'Weak (min 12 characters)';
    if (password.length < 16) return 'Good';
    return 'Strong';
  };

  return (
    <div className="min-h-screen bg-background-cream flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-soft-lg p-8 w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Logo size="lg" />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-display font-bold text-primary-500 mb-2">
            Join StorySprout
          </h1>
          <p className="text-emerald-600">
            Create personalized audiobooks for your children
          </p>
          {isDev && (
            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-300 rounded-lg">
              <p className="text-xs text-yellow-800">
                <strong>Dev Mode:</strong> Password pre-filled with 'Foundation1!'
              </p>
            </div>
          )}
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl">
            <p className="text-green-700 text-sm">{success}</p>
            <p className="text-green-600 text-xs mt-1">Redirecting to login...</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-semibold text-emerald-600 mb-2"
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-semibold text-emerald-600 mb-2"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              placeholder="••••••••••••"
              required
              autoComplete="new-password"
            />
            {/* Password Strength Indicator */}
            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getPasswordStrengthColor()} transition-all duration-300`}
                      style={{ width: `${Math.min((password.length / 16) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-emerald-600 font-semibold">
                    {getPasswordStrengthText()}
                  </span>
                </div>
              </div>
            )}
            <p className="mt-2 text-xs text-emerald-600">
              Must be at least 12 characters with uppercase, lowercase, number, and special character
            </p>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-semibold text-emerald-600 mb-2"
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input-field"
              placeholder="••••••••••••"
              required
              autoComplete="new-password"
            />
          </div>

          {/* Terms and Conditions */}
          <div className="flex items-start">
            <input
              id="terms"
              type="checkbox"
              required
              className="w-4 h-4 mt-1 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="terms" className="ml-2 text-sm text-emerald-600">
              I agree to the{' '}
              <a href="/terms" className="text-primary-500 hover:text-primary-600 font-semibold">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-primary-500 hover:text-primary-600 font-semibold">
                Privacy Policy
              </a>
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        {/* Divider */}
        <div className="my-6 flex items-center">
          <div className="flex-1 border-t-2 border-gray-200"></div>
          <span className="px-4 text-emerald-600 text-sm">or</span>
          <div className="flex-1 border-t-2 border-gray-200"></div>
        </div>

        {/* Login Link */}
        <div className="text-center">
          <p className="text-emerald-600">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-primary-500 hover:text-primary-600 font-semibold"
            >
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
